import { ArrowLeft, Pencil, Send, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { controlesDe } from '@/api/client';
import { useActionsConge, useConge } from '@/api/hooks';
import { LIBELLE_ETAPE, LIBELLE_SOUS_MOTIF, type DecisionApi } from '@/api/types';
import { useSession } from '@/auth/session';
import { BoutonPdf, Chargement, CircuitTimeline, ConfirmationDialog, DecisionDialog, ErreurBloc, Historique, PageHeader } from '@/components/common';
import { PiecesJointes } from '@/components/pieces-jointes';
import { BlocSolde, ControlesConge, EtapesPrevues, StatutCongeBadge } from '@/components/rh';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { dateFr, dateHeureFr } from '@/lib/utils';

type Dialogue = 'decision' | 'soumettre' | 'resoumettre' | 'retirer' | 'annuler' | 'supprimer' | 'interruption' | 'approuverInterruption' | 'enregistrer' | null;

/** Fiche d'une demande de congé (§ 4.4 à 4.6) : détail, solde, pièces, circuit, décisions et actions selon les droits. */
export function CongeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const moi = useSession()!.utilisateur;
  const { data, isLoading, error } = useConge(id);
  const actions = useActionsConge();
  const [dialogue, setDialogue] = useState<Dialogue>(null);
  const [motif, setMotif] = useState('');
  const [dateInterruption, setDateInterruption] = useState('');
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const { conge: c, circuit, actions: a } = data;
  const mien = c.agent.id === moi.agentId;
  const erreur = (e: unknown) => {
    const ctr = controlesDe(e);
    toast.error(e instanceof Error ? e.message : 'Action impossible.', { description: ctr.length ? ctr.map((x) => x.message).join(' · ') : undefined });
  };
  const executer = async (fn: () => Promise<unknown>, succes: string) => {
    try {
      await fn();
      toast.success(succes);
      setDialogue(null);
      setMotif('');
    } catch (e) {
      erreur(e);
    }
  };
  const decider = (decision: DecisionApi, m?: string) => executer(() => actions.decider.mutateAsync({ id: c.id, decision, motif: m }), decision === 'VALIDER' ? 'Décision enregistrée.' : 'Décision enregistrée, le demandeur est informé.');
  const retour = mien ? '/rh' : '/rh/conges';
  return (
    <div className="space-y-6">
      <PageHeader
        retour={
          <Link to={retour} className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> {mien ? 'Mes démarches RH' : 'Congés'}
          </Link>
        }
        titre={
          <span className="flex flex-wrap items-center gap-2">
            {c.numero ?? 'Demande de congé (brouillon)'} <StatutCongeBadge statut={c.statut} />
            {c.statut === 'SOUMIS' && c.etapeCourante && <Badge variant="secondary">{LIBELLE_ETAPE[c.etapeCourante]}</Badge>}
            {c.version > 1 && <Badge variant="outline">version {c.version}</Badge>}
            {c.avantDouzeMois && <Badge variant="outline">avant douze mois de service</Badge>}
            {c.nonPreste && <Badge variant="outline">jours non prestés</Badge>}
          </span>
        }
        sousTitre={`${c.libelleCategorie} · ${c.agent.nom} · ${c.structure.libelle}${c.dateSoumission ? ` · soumise le ${dateHeureFr(c.dateSoumission)}` : ''}${c.dateDecision ? ` · décidée le ${dateHeureFr(c.dateDecision)}` : ''}`}
        actions={
          <>
            {c.statut !== 'BROUILLON' && <BoutonPdf chemin={`/pdf/conges/${c.id}`} nom={`${c.numero ?? 'CG'}.pdf`} libelle="Fiche PDF" />}
            {a.modifier && (
              <Button variant="outline" asChild>
                <Link to={`/rh/conges/${c.id}/modifier`}>
                  <Pencil className="size-4" /> Modifier
                </Link>
              </Button>
            )}
            {mien && c.statut === 'BROUILLON' && (
              <Button variant="outline" onClick={() => setDialogue('supprimer')}>
                <Trash2 className="size-4" /> Supprimer
              </Button>
            )}
            {a.soumettre && (
              <Button onClick={() => setDialogue('soumettre')}>
                <Send className="size-4" /> Soumettre
              </Button>
            )}
            {a.resoumettre && (
              <Button onClick={() => setDialogue('resoumettre')}>
                <Send className="size-4" /> Soumettre à nouveau
              </Button>
            )}
            {a.retirer && (
              <Button variant="outline" onClick={() => setDialogue('retirer')}>
                Retirer
              </Button>
            )}
            {a.annuler && (
              <Button variant="outline" onClick={() => setDialogue('annuler')}>
                Annuler le congé
              </Button>
            )}
            {a.decider && <Button onClick={() => setDialogue('decision')}>Décider</Button>}
            {a.enregistrerRh && (
              <Button variant="outline" onClick={() => setDialogue('enregistrer')}>
                Enregistrer (RH)
              </Button>
            )}
            {a.demanderInterruption && (
              <Button variant="outline" onClick={() => setDialogue('interruption')}>
                Demander une interruption
              </Button>
            )}
            {a.approuverInterruption && <Button onClick={() => setDialogue('approuverInterruption')}>Approuver l'interruption</Button>}
            {a.nouvelleVersion && (
              <Button variant="outline" asChild>
                <Link to={`/rh/conges/${c.id}/nouvelle-version`}>Nouvelle version</Link>
              </Button>
            )}
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card data-testid="conge-absence">
            <CardHeader>
              <CardTitle>Absence</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
              <div><span className="text-muted-foreground">Catégorie</span><div>{c.libelleCategorie}{c.sousMotif ? ` · ${LIBELLE_SOUS_MOTIF[c.sousMotif]}` : ''}</div></div>
              <div><span className="text-muted-foreground">Jours ouvrables d'absence</span><div className="font-medium">{c.joursOuvrables}{c.joursPris !== null && c.joursPris !== c.joursOuvrables ? ` (${c.joursPris} pris)` : ''}</div></div>
              <div><span className="text-muted-foreground">Début d'absence</span><div>{dateFr(c.dateDebut)}</div></div>
              <div><span className="text-muted-foreground">Reprise de service</span><div>{dateFr(c.dateReprise)} (dernier jour d'absence le {dateFr(c.dernierJour)})</div></div>
              <div><span className="text-muted-foreground">Intérim assuré par</span><div>{c.interimaire?.nom ?? '—'}</div></div>
              <div><span className="text-muted-foreground">Enregistrement RH</span><div>{c.enregistreRhLe ? dateHeureFr(c.enregistreRhLe) : 'non enregistré'}</div></div>
              {data.feries.length > 0 && <div className="sm:col-span-2"><span className="text-muted-foreground">Fériés exclus</span><div>{data.feries.map((f) => `${dateFr(f.date)} (${f.libelle})`).join(', ')}</div></div>}
              {data.peutVoirInformations && c.informations && Object.keys(c.informations).length > 0 && (
                <div className="sm:col-span-2">
                  <span className="text-muted-foreground">Informations complémentaires (accès restreint)</span>
                  <div>
                    {c.informations.certificatDu ? `Certificat du ${dateFr(c.informations.certificatDu)} au ${dateFr(c.informations.certificatAu)} · ` : ''}
                    {c.informations.praticien ? `${c.informations.praticien} · ` : ''}
                    {c.informations.dateAccouchement ? `Accouchement le ${dateFr(c.informations.dateAccouchement)} · ` : ''}
                    {c.informations.dateEvenement ? `Événement le ${dateFr(c.informations.dateEvenement)}` : ''}
                  </div>
                </div>
              )}
              {c.motif && data.peutVoirInformations && <div className="sm:col-span-2"><span className="text-muted-foreground">Motif</span><div className="whitespace-pre-line">{c.motif}</div></div>}
              {(c.versionPrecedente || c.versionSuivante) && (
                <div className="sm:col-span-2">
                  <span className="text-muted-foreground">Versions</span>
                  <div className="flex flex-wrap gap-2">
                    {c.versionPrecedente && (
                      <Link to={`/rh/conges/${c.versionPrecedente.id}`} className="text-primary hover:underline">
                        version précédente {c.versionPrecedente.numero ?? ''} ({c.versionPrecedente.statut.toLowerCase()})
                      </Link>
                    )}
                    {c.versionSuivante && (
                      <Link to={`/rh/conges/${c.versionSuivante.id}`} className="text-primary hover:underline">
                        nouvelle version {c.versionSuivante.numero ?? '(brouillon)'} ({c.versionSuivante.statut.toLowerCase()})
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {c.interruption && (
            <Card>
              <CardHeader>
                <CardTitle>Interruption</CardTitle>
                <CardDescription>{c.interruption.approuveeLe ? `Approuvée par la DRH le ${dateHeureFr(c.interruption.approuveeLe)}` : 'Demandée par le directeur, en attente de la DRH'}</CardDescription>
              </CardHeader>
              <CardContent className="text-sm">
                Reprise anticipée le {dateFr(c.interruption.dateInterruption)} · {c.interruption.motif}
                {c.interruption.joursPris !== undefined && ` · ${c.interruption.joursPris} jour(s) pris, ${c.interruption.joursRestitues ?? 0} restitué(s)`}
              </CardContent>
            </Card>
          )}

          {data.solde && (
            <Card>
              <CardHeader>
                <CardTitle>Solde de congé annuel</CardTitle>
              </CardHeader>
              <CardContent>
                <BlocSolde periode={data.solde.periode} demandes={data.solde.demandes} apres={data.solde.apres} />
              </CardContent>
            </Card>
          )}

          {data.controles.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Contrôles avant soumission</CardTitle>
              </CardHeader>
              <CardContent>
                <ControlesConge controles={data.controles} />
              </CardContent>
            </Card>
          )}

          <PiecesJointes objetType="CONGE" objetId={c.id} typesSuggeres={['JUSTIFICATIF', 'AUTRE']} description={c.categorie === 'MALADIE' || c.categorie === 'ACCOUCHEMENT' ? 'Justificatif obligatoire, visible de l’agent et des Ressources humaines seulement.' : undefined} />
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Circuit de validation</CardTitle>
              {!circuit && <CardDescription>{c.statut === 'BROUILLON' ? 'Circuit prévu à la soumission' : 'Décision hors circuit applicatif'}</CardDescription>}
            </CardHeader>
            <CardContent>{circuit ? <CircuitTimeline circuit={circuit} /> : c.statut === 'BROUILLON' ? <EtapesPrevues etapes={data.circuitPrevu} /> : <p className="text-sm text-muted-foreground">Congé enregistré directement par les Ressources humaines{c.enregistreRhLe ? ` le ${dateFr(c.enregistreRhLe)}` : ''}, sur la base de l'autorisation de l'organe compétent.</p>}</CardContent>
          </Card>
          {circuit && circuit.history.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Historique</CardTitle>
              </CardHeader>
              <CardContent>
                <Historique circuit={circuit} />
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <DecisionDialog ouvert={dialogue === 'decision'} onOpenChange={(o) => setDialogue(o ? 'decision' : null)} decisions={a.decisions} etape={a.etape ?? c.etapeCourante} onConfirmer={decider} chargement={actions.decider.isPending} />
      <ConfirmationDialog ouvert={dialogue === 'soumettre'} onOpenChange={(o) => setDialogue(o ? 'soumettre' : null)} titre="Soumettre la demande" description="La demande reçoit un numéro et suit son circuit de validation ; les jours demandés sont réservés sur votre solde." libelle="Soumettre" onConfirmer={() => executer(() => actions.soumettre.mutateAsync(c.id), 'Demande soumise.')} chargement={actions.soumettre.isPending} />
      <ConfirmationDialog ouvert={dialogue === 'resoumettre'} onOpenChange={(o) => setDialogue(o ? 'resoumettre' : null)} titre="Soumettre à nouveau" description="La demande revient directement au validateur qui a demandé la correction." libelle="Soumettre à nouveau" onConfirmer={() => executer(() => actions.resoumettre.mutateAsync({ id: c.id }), 'Demande soumise à nouveau.')} chargement={actions.resoumettre.isPending} />
      <ConfirmationDialog ouvert={dialogue === 'retirer'} onOpenChange={(o) => setDialogue(o ? 'retirer' : null)} titre="Retirer la demande" description="La demande quitte le circuit ; les jours réservés sont libérés." libelle="Retirer" onConfirmer={() => executer(() => actions.retirer.mutateAsync(c.id), 'Demande retirée.')} chargement={actions.retirer.isPending} />
      <ConfirmationDialog ouvert={dialogue === 'supprimer'} onOpenChange={(o) => setDialogue(o ? 'supprimer' : null)} titre="Supprimer le brouillon" description="Le brouillon est supprimé définitivement." libelle="Supprimer" onConfirmer={() => executer(async () => { await actions.supprimer.mutateAsync(c.id); navigate('/rh'); }, 'Brouillon supprimé.')} chargement={actions.supprimer.isPending} />
      <ConfirmationDialog ouvert={dialogue === 'enregistrer'} onOpenChange={(o) => setDialogue(o ? 'enregistrer' : null)} titre="Enregistrer aux Ressources humaines" description="Visa administratif après décision : l'agent est informé." libelle="Enregistrer" onConfirmer={() => executer(() => actions.enregistrer.mutateAsync(c.id), 'Demande enregistrée.')} chargement={actions.enregistrer.isPending} />
      <ConfirmationDialog ouvert={dialogue === 'approuverInterruption'} onOpenChange={(o) => setDialogue(o ? 'approuverInterruption' : null)} titre="Approuver l'interruption" description={`Reprise anticipée le ${c.interruption ? dateFr(c.interruption.dateInterruption) : ''} : les jours non pris sont restitués.`} libelle="Approuver" onConfirmer={() => executer(() => actions.approuverInterruption.mutateAsync(c.id), 'Interruption approuvée, jours restitués.')} chargement={actions.approuverInterruption.isPending} />

      <Dialog open={dialogue === 'annuler'} onOpenChange={(o) => setDialogue(o ? 'annuler' : null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Annuler le congé approuvé</DialogTitle>
            <DialogDescription>Avant le début de l'absence seulement ; les jours sont restitués et l'agent, le directeur et les RH sont informés.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="motif-annulation">Motif (10 caractères au moins)</Label>
            <Textarea id="motif-annulation" rows={3} value={motif} onChange={(e) => setMotif(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogue(null)}>
              Fermer
            </Button>
            <Button onClick={() => executer(() => actions.annuler.mutateAsync({ id: c.id, motif }), 'Congé annulé, jours restitués.')} disabled={motif.trim().length < 10 || actions.annuler.isPending}>
              Annuler le congé
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogue === 'interruption'} onOpenChange={(o) => setDialogue(o ? 'interruption' : null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Demander l'interruption du congé</DialogTitle>
            <DialogDescription>Le directeur fonctionnel demande, le Directeur RH et Juridique approuve ; les jours non pris sont restitués (point 15).</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="date-interruption">Date de reprise anticipée</Label>
              <Input id="date-interruption" type="date" value={dateInterruption} onChange={(e) => setDateInterruption(e.target.value)} min={c.dateDebut} max={c.dateReprise} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="motif-interruption">Motif (10 caractères au moins)</Label>
              <Textarea id="motif-interruption" rows={3} value={motif} onChange={(e) => setMotif(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogue(null)}>
              Fermer
            </Button>
            <Button onClick={() => executer(() => actions.demanderInterruption.mutateAsync({ id: c.id, dateInterruption, motif }), 'Interruption demandée à la DRH.')} disabled={!dateInterruption || motif.trim().length < 10 || actions.demanderInterruption.isPending}>
              Demander
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
